// ManiDine Restaurant Web / src/pages/StoreHours.jsx / Updated: 2026-03-29 17:00 MDT
import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";

import { db } from "../firebase";
import useRestaurantContext from "../hooks/useRestaurantContext";
import "../MenuManager.css";

const DAY_KEYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const defaultDayHours = { open: "11:00", close: "22:00", enabled: true };

const buildDefaultWeeklyHours = () =>
  DAY_KEYS.reduce((acc, day) => {
    acc[day.key] = { ...defaultDayHours };
    return acc;
  }, {});

const isValidTimeValue = (value) =>
  typeof value === "string" && /^\d{2}:\d{2}$/.test(value);

const toMinutes = (value) => {
  if (!isValidTimeValue(value)) return null;
  const [hours, minutes] = value.split(":").map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const validateWeeklyHours = (draft) => {
  for (const day of DAY_KEYS) {
    const value = draft?.[day.key] || defaultDayHours;
    if (value.enabled === false) continue;

    const open = value.open || "";
    const close = value.close || "";

    if (!isValidTimeValue(open) || !isValidTimeValue(close)) {
      return `${day.label}: please enter both opening and closing times.`;
    }

    const openMinutes = toMinutes(open);
    const closeMinutes = toMinutes(close);

    if (openMinutes === null || closeMinutes === null) {
      return `${day.label}: invalid time format.`;
    }

    if (openMinutes === closeMinutes) {
      return `${day.label}: opening and closing time cannot be the same.`;
    }

    if (openMinutes > closeMinutes) {
      return `${day.label}: overnight hours are not supported yet. Closing time must be later than opening time.`;
    }
  }

  return "";
};

const StoreHours = () => {
  const [hours, setHours] = useState(buildDefaultWeeklyHours);
  const [hoursDraft, setHoursDraft] = useState(buildDefaultWeeklyHours);
  const [hoursError, setHoursError] = useState("");
  const [hoursSuccess, setHoursSuccess] = useState("");
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursLoading, setHoursLoading] = useState(true);
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  useEffect(() => {
    if (restaurantLoading) {
      setHoursLoading(true);
      return () => {};
    }

    if (!restaurantId) {
      setHours(buildDefaultWeeklyHours());
      setHoursDraft(buildDefaultWeeklyHours());
      setHoursLoading(false);
      return () => {};
    }

    const restaurantRef = doc(db, "restaurants", restaurantId);
    setHoursLoading(true);
    const unsub = onSnapshot(
      restaurantRef,
      (snap) => {
        const data = snap.data() || {};
        const docHours = data.hours || {};
        const next = buildDefaultWeeklyHours();
        DAY_KEYS.forEach((day) => {
          const existing = docHours[day.key] || {};
          next[day.key] = {
            open: existing.open || defaultDayHours.open,
            close: existing.close || defaultDayHours.close,
            enabled: existing.enabled !== false,
          };
        });
        setHours(next);
        setHoursDraft(next);
        setHoursError("");
        setHoursLoading(false);
      },
      (err) => {
        console.error("Load hours failed:", err);
        setHoursError("Unable to load store hours.");
        setHoursLoading(false);
      }
    );
    return () => unsub();
  }, [restaurantId, restaurantLoading]);

  const handleHoursSave = async (e) => {
    e.preventDefault();
    setHoursError("");
    setHoursSuccess("");

    const validationError = validateWeeklyHours(hoursDraft);
    if (validationError) {
      setHoursError(validationError);
      return;
    }

    setHoursSaving(true);
    try {
      if (!restaurantId) {
        throw new Error("Missing restaurantId");
      }
      await setDoc(
        doc(db, "restaurants", restaurantId),
        { hours: hoursDraft, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setHoursSuccess("Store hours saved.");
    } catch (err) {
      console.error("Save hours failed:", err);
      setHoursError("Failed to save store hours.");
    } finally {
      setHoursSaving(false);
    }
  };

  const hoursSummary = useMemo(() => {
    const firstEnabled = DAY_KEYS.find(
      (day) => hoursDraft[day.key] && hoursDraft[day.key].enabled
    );
    if (firstEnabled) {
      const firstHours = hoursDraft[firstEnabled.key];
      return `${firstEnabled.label}: ${firstHours.open || "--:--"} – ${
        firstHours.close || "--:--"
      }`;
    }
    return "Hours not set";
  }, [hoursDraft]);

  if (restaurantLoading || hoursLoading) {
    return (
      <section className="menu-card">
        <div className="menu-card-heading">
          <div>
            <h2 className="menu-section-title">Store hours</h2>
            <p className="menu-microcopy">Loading restaurant…</p>
          </div>
        </div>
      </section>
    );
  }

  if (!restaurantId) {
    return (
      <section className="menu-card">
        <div className="menu-card-heading">
          <div>
            <h2 className="menu-section-title">Store hours</h2>
            <p className="menu-microcopy">
              No restaurant found. Please sign in again.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="menu-card">
      <div className="menu-card-heading">
        <div>
          <h2 className="menu-section-title">Store hours</h2>
          <p className="menu-microcopy">
            Set when your store is open for orders.
          </p>
        </div>
      </div>
      {(hoursError || hoursSuccess) && (
        <div className="menu-alerts">
          {hoursError && <div className="menu-alert error">{hoursError}</div>}
          {hoursSuccess && (
            <div className="menu-alert success">{hoursSuccess}</div>
          )}
        </div>
      )}
      <form className="menu-form" onSubmit={handleHoursSave}>
        {DAY_KEYS.map((day) => {
          const value = hoursDraft[day.key] || defaultDayHours;
          return (
            <div key={day.key} className="menu-hours-row">
              <label className="menu-toggle-field">
                <input
                  type="checkbox"
                  checked={value.enabled !== false}
                  onChange={(e) =>
                    setHoursDraft((prev) => ({
                      ...prev,
                      [day.key]: {
                        ...defaultDayHours,
                        ...(prev[day.key] || {}),
                        enabled: e.target.checked,
                      },
                    }))
                  }
                />
                <div>
                  <p className="menu-toggle-title">{day.label}</p>
                  <p className="menu-toggle-sub">Open this day</p>
                </div>
              </label>
              <div className="menu-form-grid">
                <label className="menu-field">
                  <span>Opens at</span>
                  <input
                    type="time"
                    value={value.open || ""}
                    disabled={value.enabled === false}
                    onChange={(e) =>
                      setHoursDraft((prev) => ({
                        ...prev,
                        [day.key]: {
                          ...defaultDayHours,
                          ...(prev[day.key] || {}),
                          open: e.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label className="menu-field">
                  <span>Closes at</span>
                  <input
                    type="time"
                    value={value.close || ""}
                    disabled={value.enabled === false}
                    onChange={(e) =>
                      setHoursDraft((prev) => ({
                        ...prev,
                        [day.key]: {
                          ...defaultDayHours,
                          ...(prev[day.key] || {}),
                          close: e.target.value,
                        },
                      }))
                    }
                  />
                </label>
              </div>
            </div>
          );
        })}
        <div className="menu-microcopy">
          Preview: {hoursSummary} (first open day)
        </div>
        <div className="menu-microcopy" style={{ marginTop: 6 }}>
          Note: same-day hours only right now. Overnight hours like 10:00 PM to 2:00 AM are not supported yet.
        </div>
        <div className="menu-form-actions">
          <button
            type="button"
            className="menu-secondary-btn"
            onClick={() => {
              setHoursDraft(hours);
              setHoursError("");
              setHoursSuccess("");
            }}
          >
            Reset
          </button>
          <button
            type="submit"
            className="menu-primary-btn"
            disabled={hoursSaving}
          >
            {hoursSaving ? "Saving…" : "Save hours"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default StoreHours;
